import * as React from 'react'
import { Auth0Provider, type AppState } from '@auth0/auth0-react'
import { getAuth0Config } from '@/lib/auth/config'
import { clearUnboundHeldTenant } from '@/lib/tenant/tenant-session'

/*
 * Wraps the app in Auth0's SPA provider using the Authorization Code + PKCE
 * flow. Refresh tokens plus a persisted cache give us silent renewal and let an
 * existing session survive a full reload or direct navigation without a fresh
 * prompt. The callback exchange is handled by the provider; onRedirectCallback
 * strips the ?code/?state params back out of the URL afterwards, and drops a
 * held tenant that cannot be told apart from this new sign-in (VIS-326).
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const onRedirectCallback = React.useCallback((appState?: AppState) => {
    clearUnboundHeldTenant()
    const target = appState?.returnTo ?? window.location.pathname
    window.history.replaceState({}, document.title, target)
  }, [])
  const auth0 = getAuth0Config()

  return (
    <Auth0Provider
      domain={auth0.domain}
      clientId={auth0.clientId}
      authorizationParams={{
        redirect_uri: typeof window !== 'undefined' ? window.location.origin : undefined,
        audience: auth0.audience,
      }}
      useRefreshTokens
      useRefreshTokensFallback
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  )
}
